import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../components/MainLayout';
import { apiUrls } from '../utils/apiUrls';
import { callAPI } from '../utils/apiUtils';
import { toast } from 'react-toastify';
import ZoneTabBar from '../components/floorplan/ZoneTabBar';
import FloorPlanCanvas from '../components/floorplan/FloorPlanCanvas';
import ZonePropertiesModal from '../components/modals/ZonePropertiesModal';
import TablePropertiesModal from '../components/modals/TablePropertiesModal';
import ElementPropertiesModal from '../components/modals/ElementPropertiesModal';
import FloorPlanImportModal from '../components/modals/FloorPlanImportModal';
import ValidatePinModal from '../components/modals/ValidatePinModal';
import useRequirePermission from '../hooks/useRequirePermission';
import '../styles/floorPlan.css';

const groupByZone = (items) => {
  const grouped = {};
  items.forEach((item) => {
    const zoneId = item.zone_id;
    if (!grouped[zoneId]) grouped[zoneId] = [];
    grouped[zoneId].push(item);
  });
  return grouped;
};

const FloorPlanEditorPage = () => {
  const [loading, setLoading] = useState(true);
  const [zones, setZones] = useState([]);
  const [specialZones, setSpecialZones] = useState([]);
  const [tablesByZone, setTablesByZone] = useState({});
  const [elementsByZone, setElementsByZone] = useState({});
  const [activeZoneId, setActiveZoneId] = useState(null);
  // Arrays, not a single id - lets staff build up a group (shift/ctrl+click) spanning both
  // tables and elements and move all of them together, see handleSelectTable/handleSelectElement
  // and the arrow-key nudge effect below.
  const [selectedTableIds, setSelectedTableIds] = useState([]);
  const [selectedElementIds, setSelectedElementIds] = useState([]);
  const [dirtyTableIds, setDirtyTableIds] = useState(new Set());
  const [dirtyElementIds, setDirtyElementIds] = useState(new Set());
  const [savedColors, setSavedColors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [duplicating, setDuplicating] = useState(false);

  const [zoneModal, setZoneModal] = useState({ open: false, zone: null });
  const [tableModal, setTableModal] = useState({ open: false, table: null });
  const [elementModal, setElementModal] = useState({ open: false, element: null });
  const [importModalOpen, setImportModalOpen] = useState(false);
  // Tables detected by "Import Floor Plan" (see FloorPlanImportModal/FloorPlanImportService) -
  // kept entirely separate from tablesByZone until confirmed, so a not-yet-reviewed AI guess can
  // never get swept into the real dirty-tracking/Save Layout flow or persisted by accident. Each
  // has a synthetic string id (e.g. "draft-1") from the backend, never a real numeric table id.
  const [draftTables, setDraftTables] = useState([]);
  const [selectedDraftTableId, setSelectedDraftTableId] = useState(null);
  const [confirmingImport, setConfirmingImport] = useState(false);

  const { requirePermission, permissionModalProps } = useRequirePermission();
  const navigate = useNavigate();
  const [accessGranted, setAccessGranted] = useState(false);

  const loadFloorPlan = useCallback(async () => {
    setLoading(true);
    try {
      const res = await callAPI(apiUrls.FLOOR_PLAN_BOOTSTRAP, {}, 'GET');
      const data = res.data?.data || {};
      const fetchedZones = data.zones || [];
      const fetchedSpecialZones = data.specialZones || [];
      const fetchedTables = data.tables || [];
      const fetchedElements = data.elements || [];

      setZones(fetchedZones);
      setSpecialZones(fetchedSpecialZones);
      setTablesByZone(groupByZone(fetchedTables));
      setElementsByZone(groupByZone(fetchedElements));
      setSavedColors(data.savedColors || []);
      // Falls back to the first special tab (Person/Tabs/Takeaway) when a restaurant has none of
      // the physical seating zones this editor defaults to, so it never opens with a tab bar but
      // nothing selected.
      setActiveZoneId((prev) => prev || (fetchedZones[0] || fetchedSpecialZones[0])?.id);
    } catch (err) {
      console.error('Failed to load floor plan', err);
      toast.error('Failed to load floor plan');
    } finally {
      setLoading(false);
    }
  }, []);

  // Editing the floor plan is a manager-level action — check on entry, before loading any data,
  // rather than only gating the final Save (which would let unauthorized staff freely see/use
  // the whole editor and only get blocked at the last step).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { authorized } = await requirePermission('floorplan.edit');
      if (cancelled) return;
      if (!authorized) {
        toast.error('You are not authorized to edit the floor plan.');
        navigate('/dashboard');
        return;
      }
      setAccessGranted(true);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (accessGranted) {
      loadFloorPlan();
    }
  }, [accessGranted, loadFloorPlan]);

  const activeTables = tablesByZone[activeZoneId] || [];
  const activeElements = elementsByZone[activeZoneId] || [];
  const activeSpecialZone = specialZones.find((z) => z.id === activeZoneId);
  const isSpecialZoneActive = !!activeSpecialZone;
  const isPagerZoneActive = activeSpecialZone?.value === 'pager-tab';

  const updateTableLocally = (tableId, changes) => {
    setTablesByZone((prev) => {
      const updated = { ...prev };
      updated[activeZoneId] = (updated[activeZoneId] || []).map((t) =>
        t.id === tableId ? { ...t, ...changes } : t
      );
      return updated;
    });
    setDirtyTableIds((prev) => new Set(prev).add(tableId));
  };

  const updateElementLocally = (elementId, changes) => {
    setElementsByZone((prev) => {
      const updated = { ...prev };
      updated[activeZoneId] = (updated[activeZoneId] || []).map((el) =>
        el.id === elementId ? { ...el, ...changes } : el
      );
      return updated;
    });
    setDirtyElementIds((prev) => new Set(prev).add(elementId));
  };

  const toggleId = (ids, id) => (ids.includes(id) ? ids.filter((existing) => existing !== id) : [...ids, id]);

  // A plain click replaces the whole selection with just this item (table or element - clicking
  // a table clears any selected elements and vice versa, since that's the "start a new pick"
  // gesture). Shift/ctrl/cmd+click instead toggles this one item into or out of whatever's
  // already selected, without touching the other list - that's what lets a group span both
  // tables and elements at once, for the arrow-key move-together feature below.
  const handleSelectTable = (tableId, e) => {
    const additive = e && (e.shiftKey || e.ctrlKey || e.metaKey);
    if (additive) {
      setSelectedTableIds((prev) => toggleId(prev, tableId));
    } else {
      setSelectedTableIds([tableId]);
      setSelectedElementIds([]);
    }
    setSelectedDraftTableId(null);
  };
  const handleSelectElement = (elementId, e) => {
    const additive = e && (e.shiftKey || e.ctrlKey || e.metaKey);
    if (additive) {
      setSelectedElementIds((prev) => toggleId(prev, elementId));
    } else {
      setSelectedElementIds([elementId]);
      setSelectedTableIds([]);
    }
    setSelectedDraftTableId(null);
  };
  const handleDeselect = () => {
    setSelectedTableIds([]);
    setSelectedElementIds([]);
    setSelectedDraftTableId(null);
  };

  // Draft tables (a pending, unconfirmed "Import Floor Plan" result) get their own tiny
  // select/update pair instead of reusing updateTableLocally/handleSelectTable - they're not in
  // tablesByZone at all, so the real dirty-tracking Set and Save Layout flow never see them.
  const updateDraftTableLocally = (tempId, changes) => {
    setDraftTables((prev) => prev.map((t) => (t.id === tempId ? { ...t, ...changes } : t)));
  };
  const handleSelectDraftTable = (tempId) => {
    setSelectedDraftTableId(tempId);
    setSelectedTableIds([]);
    setSelectedElementIds([]);
  };

  const handleAddTable = () => {
    if (!activeZoneId) {
      toast.warning('Create a zone first');
      return;
    }
    setTableModal({ open: true, table: null });
  };

  const handleAddElement = () => {
    if (!activeZoneId) {
      toast.warning('Create a zone first');
      return;
    }
    setElementModal({ open: true, element: null });
  };

  const handleOpenImportModal = () => {
    if (!activeZoneId) {
      toast.warning('Create a zone first');
      return;
    }
    setImportModalOpen(true);
  };

  const handleImportAnalyzed = (data) => {
    const tables = (data?.tables || []).map((t) => ({
      id: t.temp_id,
      name: t.name,
      shape: t.shape,
      number_of_people: t.number_of_people,
      width: t.width,
      height: t.height,
      position_x: t.position_x,
      position_y: t.position_y,
      rotation: t.rotation || 0,
    }));

    setImportModalOpen(false);

    if (tables.length === 0) {
      toast.warning('No tables were detected in that file - try a clearer image or PDF.');
      return;
    }

    setDraftTables(tables);
    setSelectedDraftTableId(null);
    toast.success(`Detected ${tables.length} table${tables.length === 1 ? '' : 's'} - review and adjust below, then confirm.`);
  };

  const handleRemoveSelectedDraft = () => {
    setDraftTables((prev) => prev.filter((t) => t.id !== selectedDraftTableId));
    setSelectedDraftTableId(null);
  };

  const handleDiscardImport = () => {
    setDraftTables([]);
    setSelectedDraftTableId(null);
  };

  // Confirming creates each draft for real, one at a time (not in parallel) - sequential calls
  // mean a name collision within the batch surfaces as a normal per-table validation error rather
  // than a race, same as the backend's existing unique-per-zone check already assumes. Anything
  // that fails stays in the draft list (with its position/size intact) so the manager can rename
  // or nudge just that one and confirm again, rather than losing the whole batch over one clash.
  const handleConfirmImport = async () => {
    if (draftTables.length === 0) return;

    const { authorized } = await requirePermission('floorplan.edit');
    if (!authorized) return;

    setConfirmingImport(true);
    let successCount = 0;
    const remaining = [];

    for (const draft of draftTables) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const res = await callAPI(apiUrls.FLOOR_PLAN_CREATE_TABLE, {}, 'POST', {
          zone_id: activeZoneId,
          name: draft.name,
          shape: draft.shape,
          number_of_people: draft.number_of_people,
          width: draft.width,
          height: draft.height,
          rotation: draft.rotation,
          color: null,
          position_x: draft.position_x,
          position_y: draft.position_y,
        }, {});
        handleTableSaved(res.data.data, true);
        successCount++;
      } catch (err) {
        console.error('Failed to create imported table', draft, err);
        remaining.push(draft);
      }
    }

    setDraftTables(remaining);
    setSelectedDraftTableId(null);
    setConfirmingImport(false);

    if (remaining.length === 0) {
      toast.success(`Added ${successCount} table${successCount === 1 ? '' : 's'} from the import.`);
    } else {
      toast.warning(`Added ${successCount} table(s); ${remaining.length} failed (often a name clash) - adjust and confirm again.`);
    }
  };

  const handleEditSelectedTable = () => {
    if (selectedTableIds.length !== 1) return;
    const table = activeTables.find((t) => t.id === selectedTableIds[0]);
    if (table) setTableModal({ open: true, table });
  };

  const handleEditSelectedElement = () => {
    if (selectedElementIds.length !== 1) return;
    const element = activeElements.find((el) => el.id === selectedElementIds[0]);
    if (element) setElementModal({ open: true, element });
  };

  // Offset so the copy lands visibly next to the original instead of exactly on top of it.
  const DUPLICATE_OFFSET = 30;

  // Table names are unique per zone and capped at 3 alphanumeric characters (Table::rules()),
  // so a duplicate can never reuse the original's name outright - it always collides and the
  // backend rejects it. Free up room for a numeric suffix by trimming the base name, then try
  // increasing suffixes until one isn't already taken in this zone.
  const generateDuplicateName = (baseName, existingNames) => {
    if (!existingNames.includes(baseName)) return baseName;

    for (let suffix = 2; suffix <= 9; suffix++) {
      const suffixStr = String(suffix);
      const candidate = baseName.slice(0, 3 - suffixStr.length) + suffixStr;
      if (!existingNames.includes(candidate)) return candidate;
    }

    return null;
  };

  const handleDuplicateTable = async () => {
    if (selectedTableIds.length !== 1) return;
    const table = activeTables.find((t) => t.id === selectedTableIds[0]);
    if (!table || duplicating) return;

    const existingNames = activeTables.map((t) => t.name);
    const newName = generateDuplicateName(table.name, existingNames);
    if (!newName) {
      toast.error('Could not find a free table name in this zone - rename some tables and try again.');
      return;
    }

    setDuplicating(true);
    try {
      const res = await callAPI(apiUrls.FLOOR_PLAN_CREATE_TABLE, {}, 'POST', {
        zone_id: table.zone_id,
        name: newName,
        shape: table.shape,
        number_of_people: table.number_of_people,
        width: table.width,
        height: table.height,
        rotation: table.rotation,
        color: table.color || null,
        position_x: table.position_x + DUPLICATE_OFFSET,
        position_y: table.position_y + DUPLICATE_OFFSET,
      }, {});

      const duplicated = res.data.data;
      handleTableSaved(duplicated, true);
      setSelectedTableIds([duplicated.id]);
      setSelectedElementIds([]);
      toast.success('Table duplicated');
    } catch (err) {
      console.error('Failed to duplicate table', err);
      toast.error('Failed to duplicate table');
    } finally {
      setDuplicating(false);
    }
  };

  const handleDuplicateElement = async () => {
    if (selectedElementIds.length !== 1) return;
    const element = activeElements.find((el) => el.id === selectedElementIds[0]);
    if (!element || duplicating) return;

    setDuplicating(true);
    try {
      const res = await callAPI(apiUrls.FLOOR_PLAN_CREATE_ELEMENT, {}, 'POST', {
        zone_id: element.zone_id,
        type: element.type,
        label: element.label || null,
        color: element.color || null,
        width: element.width,
        height: element.height,
        rotation: element.rotation,
        position_x: element.position_x + DUPLICATE_OFFSET,
        position_y: element.position_y + DUPLICATE_OFFSET,
      }, {});

      const duplicated = res.data.data;
      handleElementSaved(duplicated, true);
      setSelectedElementIds([duplicated.id]);
      setSelectedTableIds([]);
      toast.success('Element duplicated');
    } catch (err) {
      console.error('Failed to duplicate element', err);
      toast.error('Failed to duplicate element');
    } finally {
      setDuplicating(false);
    }
  };

  const handleTableSaved = (savedTable, isNew) => {
    setTablesByZone((prev) => {
      const updated = { ...prev };
      const zoneId = savedTable.zone_id;
      const list = updated[zoneId] || [];
      updated[zoneId] = isNew
        ? [...list, savedTable]
        : list.map((t) => (t.id === savedTable.id ? savedTable : t));
      return updated;
    });
    if (savedTable.color) {
      setSavedColors((prev) => (prev.includes(savedTable.color) ? prev : [savedTable.color, ...prev]));
    }
    setTableModal({ open: false, table: null });
  };

  const handleElementSaved = (savedElement, isNew) => {
    setElementsByZone((prev) => {
      const updated = { ...prev };
      const zoneId = savedElement.zone_id;
      const list = updated[zoneId] || [];
      updated[zoneId] = isNew
        ? [...list, savedElement]
        : list.map((el) => (el.id === savedElement.id ? savedElement : el));
      return updated;
    });
    if (savedElement.color) {
      setSavedColors((prev) => (prev.includes(savedElement.color) ? prev : [savedElement.color, ...prev]));
    }
    setElementModal({ open: false, element: null });
  };

  const handleTableDeleted = (tableId) => {
    setTablesByZone((prev) => {
      const updated = { ...prev };
      updated[activeZoneId] = (updated[activeZoneId] || []).filter((t) => t.id !== tableId);
      return updated;
    });
    setDirtyTableIds((prev) => {
      const next = new Set(prev);
      next.delete(tableId);
      return next;
    });
    setSelectedTableIds((prev) => prev.filter((id) => id !== tableId));
    setTableModal({ open: false, table: null });
  };

  const handleElementDeleted = (elementId) => {
    setElementsByZone((prev) => {
      const updated = { ...prev };
      updated[activeZoneId] = (updated[activeZoneId] || []).filter((el) => el.id !== elementId);
      return updated;
    });
    setDirtyElementIds((prev) => {
      const next = new Set(prev);
      next.delete(elementId);
      return next;
    });
    setSelectedElementIds((prev) => prev.filter((id) => id !== elementId));
    setElementModal({ open: false, element: null });
  };

  const handleSaveLayout = async () => {
    if (dirtyTableIds.size === 0 && dirtyElementIds.size === 0) {
      toast.info('Nothing to save');
      return;
    }

    const { authorized } = await requirePermission('floorplan.edit');
    if (!authorized) {
      return;
    }

    const tablePayload = Array.from(dirtyTableIds)
      .map((id) => activeTables.find((t) => t.id === id))
      .filter(Boolean)
      .map((t) => ({
        id: t.id,
        position_x: t.position_x,
        position_y: t.position_y,
        width: t.width,
        height: t.height,
        rotation: t.rotation,
      }));

    const elementPayload = Array.from(dirtyElementIds)
      .map((id) => activeElements.find((el) => el.id === id))
      .filter(Boolean)
      .map((el) => ({
        id: el.id,
        position_x: el.position_x,
        position_y: el.position_y,
        width: el.width,
        height: el.height,
        rotation: el.rotation,
      }));

    setSaving(true);
    try {
      if (tablePayload.length > 0) {
        await callAPI(apiUrls.FLOOR_PLAN_SAVE_LAYOUT, {}, 'POST', tablePayload, {});
      }
      if (elementPayload.length > 0) {
        await callAPI(apiUrls.FLOOR_PLAN_SAVE_ELEMENTS_LAYOUT, {}, 'POST', elementPayload, {});
      }
      toast.success('Layout saved');
      setDirtyTableIds(new Set());
      setDirtyElementIds(new Set());
    } catch (err) {
      console.error('Failed to save layout', err);
      toast.error('Failed to save layout');
    } finally {
      setSaving(false);
    }
  };

  const isDirty = dirtyTableIds.size > 0 || dirtyElementIds.size > 0;

  const selectionCount = selectedTableIds.length + selectedElementIds.length;

  // Arrow keys nudge every selected table/element together by the same delta, routed through the
  // same updateTableLocally/updateElementLocally functions a single drag uses - so the existing
  // dirty-tracking and Save Layout flow picks up a group move with no changes of its own. Held
  // Shift takes a bigger step, matching the "big jump vs fine adjustment" convention most design
  // tools use for arrow-key nudging.
  useEffect(() => {
    if (selectionCount === 0) return undefined;

    const NUDGE_STEP = 5;
    const NUDGE_STEP_FAST = 25;

    const handleKeyDown = (e) => {
      if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) return;
      // Modals (table/element/zone properties, PIN entry) and any text field can themselves use
      // arrow keys - only hijack them when focus is out on the canvas, not inside a form control.
      if (zoneModal.open || tableModal.open || elementModal.open) return;
      const tag = document.activeElement && document.activeElement.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      e.preventDefault();
      const step = e.shiftKey ? NUDGE_STEP_FAST : NUDGE_STEP;
      const dx = e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0;
      const dy = e.key === 'ArrowUp' ? -step : e.key === 'ArrowDown' ? step : 0;

      selectedTableIds.forEach((id) => {
        const table = activeTables.find((t) => t.id === id);
        if (!table) return;
        updateTableLocally(id, {
          position_x: Math.max(0, Math.round(table.position_x + dx)),
          position_y: Math.max(0, Math.round(table.position_y + dy)),
        });
      });
      selectedElementIds.forEach((id) => {
        const element = activeElements.find((el) => el.id === id);
        if (!element) return;
        updateElementLocally(id, {
          position_x: Math.max(0, Math.round(element.position_x + dx)),
          position_y: Math.max(0, Math.round(element.position_y + dy)),
        });
      });
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionCount, selectedTableIds, selectedElementIds, activeTables, activeElements, zoneModal.open, tableModal.open, elementModal.open]);

  const reviewingImport = draftTables.length > 0;

  const footerButtons = reviewingImport ? (
    <>
      <span className="floorPlanSelectionHint">
        {draftTables.length} table{draftTables.length === 1 ? '' : 's'} detected — drag/resize to adjust, then confirm
      </span>
      {selectedDraftTableId && (
        <button className="btn iconBtn" type="button" onClick={handleRemoveSelectedDraft} disabled={confirmingImport}>
          <span>Remove Selected</span>
        </button>
      )}
      <button className="btn iconBtn" type="button" onClick={handleDiscardImport} disabled={confirmingImport}>
        <span>Discard Import</span>
      </button>
      <button
        className="btn iconBtn btnPrimary"
        type="button"
        onClick={handleConfirmImport}
        disabled={confirmingImport}
      >
        <span>
          {confirmingImport ? 'Adding...' : `Add These ${draftTables.length} Table${draftTables.length === 1 ? '' : 's'}`}
        </span>
      </button>
    </>
  ) : (
    <>
      <button className="btn iconBtn" type="button" onClick={handleAddTable}>
        <span>{isPagerZoneActive ? 'Add Pager' : 'Add Table'}</span>
      </button>
      {!isSpecialZoneActive && (
        <button className="btn iconBtn" type="button" onClick={handleAddElement}>
          <span>Add Element</span>
        </button>
      )}
      {!isSpecialZoneActive && (
        <button className="btn iconBtn" type="button" onClick={handleOpenImportModal}>
          <span>Import Floor Plan</span>
        </button>
      )}
      {selectedTableIds.length === 1 && selectedElementIds.length === 0 && (
        <>
          <button className="btn iconBtn" type="button" onClick={handleEditSelectedTable}>
            <span>Edit Table</span>
          </button>
          <button className="btn iconBtn" type="button" onClick={handleDuplicateTable} disabled={duplicating}>
            <span>{duplicating ? 'Duplicating...' : 'Duplicate Table'}</span>
          </button>
        </>
      )}
      {selectedElementIds.length === 1 && selectedTableIds.length === 0 && (
        <>
          <button className="btn iconBtn" type="button" onClick={handleEditSelectedElement}>
            <span>Edit Element</span>
          </button>
          <button className="btn iconBtn" type="button" onClick={handleDuplicateElement} disabled={duplicating}>
            <span>{duplicating ? 'Duplicating...' : 'Duplicate Element'}</span>
          </button>
        </>
      )}
      {selectionCount > 1 && (
        <span className="floorPlanSelectionHint">
          {selectionCount} selected — arrow keys to move together, click empty space to clear
        </span>
      )}
      <button
        className="btn iconBtn btnPrimary"
        type="button"
        onClick={handleSaveLayout}
        disabled={!isDirty || saving}
      >
        <span>{saving ? 'Saving...' : 'Save Layout'}</span>
      </button>
    </>
  );

  return (
    <MainLayout footerButtons={footerButtons}>
      <div className="pageContent floorPlanPage floorPlanEditorView">
        <div className="container">
          {loading ? (
            <p>Loading floor plan...</p>
          ) : (
            <>
              <ZoneTabBar
                zones={zones}
                activeZoneId={activeZoneId}
                onSelectZone={(id) => {
                  if (draftTables.length > 0) {
                    toast.warning('Confirm or discard the pending floor plan import before switching zones.');
                    return;
                  }
                  setActiveZoneId(id);
                  setSelectedTableIds([]);
                  setSelectedElementIds([]);
                }}
                onZonesReordered={setZones}
                onAddZone={() => setZoneModal({ open: true, zone: null })}
                onEditZone={(zone) => setZoneModal({ open: true, zone })}
                extraTabs={specialZones}
              />

              {isSpecialZoneActive ? (
                <div className="specialZoneTableList">
                  {activeTables.map((table) => (
                    <button
                      key={table.id}
                      type="button"
                      className={`specialZoneTableCard${selectedTableIds.includes(table.id) ? ' selected' : ''}`}
                      onClick={(e) => handleSelectTable(table.id, e)}
                    >
                      <span className="specialZoneTableName">{table.name}</span>
                      {!isPagerZoneActive && (
                        <span className="specialZoneTableSeats">
                          <svg width="11" height="10" className="personIcon">
                            <use xlinkHref="/images/sprite.svg#iconPersons" />
                          </svg>
                          {table.number_of_people}
                        </span>
                      )}
                    </button>
                  ))}
                  {activeTables.length === 0 && (
                    <p>{isPagerZoneActive ? 'No pagers yet — use Add Pager below.' : 'No tables yet — use Add Table below.'}</p>
                  )}
                </div>
              ) : (
                <FloorPlanCanvas
                  tables={activeTables}
                  elements={activeElements}
                  selectedTableIds={selectedTableIds}
                  selectedElementIds={selectedElementIds}
                  onSelectTable={handleSelectTable}
                  onSelectElement={handleSelectElement}
                  onDeselect={handleDeselect}
                  onTableChange={updateTableLocally}
                  onElementChange={updateElementLocally}
                  draftTables={draftTables}
                  selectedDraftTableId={selectedDraftTableId}
                  onSelectDraftTable={handleSelectDraftTable}
                  onDraftTableChange={updateDraftTableLocally}
                />
              )}
            </>
          )}
        </div>

        <ZonePropertiesModal
          isOpen={zoneModal.open}
          zone={zoneModal.zone}
          isSpecial={!!zoneModal.zone && specialZones.some((z) => z.id === zoneModal.zone.id)}
          onClose={() => setZoneModal({ open: false, zone: null })}
          onSaved={(zone, isNew) => {
            if (!isNew && specialZones.some((z) => z.id === zone.id)) {
              setSpecialZones((prev) => prev.map((z) => (z.id === zone.id ? zone : z)));
            } else {
              setZones((prev) =>
                isNew ? [...prev, zone] : prev.map((z) => (z.id === zone.id ? zone : z))
              );
            }
            if (isNew) setActiveZoneId(zone.id);
            setZoneModal({ open: false, zone: null });
          }}
          onDeleted={(zoneId) => {
            setZones((prev) => prev.filter((z) => z.id !== zoneId));
            setTablesByZone((prev) => {
              const updated = { ...prev };
              delete updated[zoneId];
              return updated;
            });
            setElementsByZone((prev) => {
              const updated = { ...prev };
              delete updated[zoneId];
              return updated;
            });
            setActiveZoneId((prev) => (prev === zoneId ? null : prev));
            setZoneModal({ open: false, zone: null });
          }}
        />

        <TablePropertiesModal
          isOpen={tableModal.open}
          table={tableModal.table}
          zoneId={activeZoneId}
          zoneValue={activeSpecialZone?.value}
          savedColors={savedColors}
          onClose={() => setTableModal({ open: false, table: null })}
          onSaved={handleTableSaved}
          onDeleted={handleTableDeleted}
        />

        <ElementPropertiesModal
          isOpen={elementModal.open}
          element={elementModal.element}
          zoneId={activeZoneId}
          savedColors={savedColors}
          onClose={() => setElementModal({ open: false, element: null })}
          onSaved={handleElementSaved}
          onDeleted={handleElementDeleted}
        />

        <FloorPlanImportModal
          isOpen={importModalOpen}
          onClose={() => setImportModalOpen(false)}
          onAnalyzed={handleImportAnalyzed}
        />

        <ValidatePinModal {...permissionModalProps} />
      </div>
    </MainLayout>
  );
};

export default FloorPlanEditorPage;
